/**
 * Helpers puros del módulo de Contratos: estado inicial del formulario
 * y cálculo del preview (subtotal, descuento, precio neto).
 *
 * No tienen efectos secundarios — son funciones puras testeables.
 */

export const emptyContractForm = {
  client_name: "",
  client_phone: "",
  client_email: "",
  event_name: "",
  salon: "",
  event_date: "",
  event_time: "",
  service_time: "",
  service_time_comment: "",
  publication_authorization: "pendiente",
  contract_type: "public",
  include_cabina: false,
  cabina_hours: 0,
  price_cabina: 0,
  include_video360: false,
  video360_hours: 0,
  price_video360: 0,
  include_key_moments: false,
  key_moments_pieces: 0,
  price_key_moments: 0,
  include_live: false,
  price_live: 0,
  extras: [],
  discount_amount: 0,
  special_price: null,
  notes: "",
  cortesia: "",
  manual_total: 0,
  anticipo_amount: 0,
  anticipo_status: "pendiente",
  costo_proveedor: null,
  fecha_pago: null,
};

/**
 * Calcula subtotal, descuento y precio neto de un contrato a partir del formulario.
 * Espejo idéntico de la lógica original en App.js.
 */
export const calculateContractPreview = (contractForm) => {
  let subtotal = 0;

  if (contractForm.include_cabina && contractForm.price_cabina > 0) {
    subtotal += contractForm.price_cabina;
  }
  if (contractForm.include_video360 && contractForm.price_video360 > 0) {
    subtotal += contractForm.price_video360;
  }
  if (contractForm.include_key_moments && contractForm.price_key_moments > 0) {
    subtotal += contractForm.price_key_moments;
  }
  if (contractForm.include_live) {
    subtotal += contractForm.price_live || 700;
  }

  const discountAmount = contractForm.discount_amount || 0;
  let netPrice;

  if (contractForm.contract_type === "special" && contractForm.special_price) {
    netPrice = contractForm.special_price;
  } else {
    netPrice = subtotal - discountAmount;
  }

  return { subtotal, discountAmount, netPrice };
};

/**
 * Hidrata el formulario de contrato a partir de un contrato existente
 * (para entrar en modo edición). Espejo idéntico de startEditContract.
 */
export const contractToForm = (contract) => ({
  client_name: contract.client_name || "",
  client_phone: contract.client_phone || "",
  client_email: contract.client_email || "",
  event_name: contract.event_name || "",
  salon: contract.salon || "",
  event_date: contract.event_date || "",
  event_time: contract.event_time || "",
  service_time: contract.service_time || "",
  service_time_comment: contract.service_time_comment || "",
  publication_authorization: contract.publication_authorization || "pendiente",
  contract_type: contract.contract_type || "public",
  include_cabina: contract.include_cabina || false,
  cabina_hours: contract.cabina_hours || 0,
  price_cabina: contract.price_cabina || 0,
  include_video360: contract.include_video360 || false,
  video360_hours: contract.video360_hours || 0,
  price_video360: contract.price_video360 || 0,
  include_key_moments: contract.include_key_moments || false,
  key_moments_pieces: contract.key_moments_pieces || 0,
  price_key_moments: contract.price_key_moments || 0,
  include_live: contract.include_live || false,
  price_live: contract.price_live || 700,
  extras: contract.extras || [],
  discount_amount: contract.discount_amount || 0,
  special_price: contract.special_price || null,
  notes: contract.notes || "",
  cortesia: contract.cortesia || "",
  manual_total: contract.net_price || 0,
  anticipo_amount: contract.anticipo_amount || 0,
  anticipo_status: contract.anticipo_status || "pendiente",
  costo_proveedor: contract.costo_proveedor || null,
  fecha_pago: contract.fecha_pago || null,
});

/**
 * Comparador para ordenar contratos por folio PIC-XXX descendente.
 * Los contratos sin folio van al final.
 */
export const sortByFolioDesc = (a, b) => {
  const num = (c) => {
    const f = (c.folio || "").toString();
    const n = parseInt(f.replace(/\D/g, ""), 10);
    return Number.isNaN(n) ? -1 : n;
  };
  return num(b) - num(a);
};

/**
 * Filtro en tiempo real del listado de contratos por folio o nombre de cliente.
 */
export const filterBySearchTerm = (contracts, term) => {
  const q = (term || "").trim().toLowerCase();
  if (!q) return contracts;
  return contracts.filter((c) => {
    const folio = (c.folio || "").toLowerCase();
    const name = (c.client_name || "").toLowerCase();
    return folio.includes(q) || name.includes(q);
  });
};

// ============ REPORTE DE PAGOS (abonos) ============

export const FORMA_PAGO_LABELS = { efectivo: "Efectivo", transferencia: "Transferencia", tarjeta: "Tarjeta", deposito: "Depósito", no_especificado: "No especificado" };

/** Lista de abonos de un contrato, protegiendo contratos viejos con abonos=null/undefined. */
export const contractAbonos = (contract) => contract.abonos || [];

/** Suma de todos los abonos registrados de un contrato. */
export const totalAbonado = (contract) => contractAbonos(contract).reduce((sum, a) => sum + (a.monto || 0), 0);

/** Total a cobrar: net_price ya refleja manual_total/special_price/descuento. */
export const totalContratado = (contract) => contract.net_price || 0;

/** Saldo pendiente, nunca negativo. */
export const saldoPendiente = (contract) => Math.max(0, totalContratado(contract) - totalAbonado(contract));

/**
 * Saldo restante inmediatamente después de UN abono específico (para el recibo individual),
 * sumando únicamente los abonos con fecha igual o anterior a él (orden cronológico, no el
 * saldo actual del contrato que ya incluye pagos posteriores).
 */
export const balanceAfterAbono = (contract, abonoId) => {
  const sorted = [...contractAbonos(contract)].sort((a, b) => {
    const byDate = (a.fecha || "").localeCompare(b.fecha || "");
    if (byDate !== 0) return byDate;
    return (a.created_at || "").localeCompare(b.created_at || "");
  });
  let cumulative = 0;
  for (const a of sorted) {
    cumulative += a.monto || 0;
    if (a.id === abonoId) break;
  }
  return Math.max(0, totalContratado(contract) - cumulative);
};

/**
 * Estado de pago: "cancelado" (manual, vía contract.status), "pagado" (abonado >= total)
 * o "pendiente" (saldo > 0). No depende de anticipo_status (campo aparte, de uso interno en Contratos).
 */
export const paymentStatus = (contract) => {
  if (contract.status === "cancelled") return "cancelado";
  return saldoPendiente(contract) <= 0 ? "pagado" : "pendiente";
};

export const PAYMENT_STATUS_LABELS = { pagado: "Pagado", pendiente: "Saldo pendiente", cancelado: "Cancelado" };
export const PAYMENT_STATUS_COLORS = {
  pagado: "bg-green-500/20 text-green-400 border-green-500/50",
  pendiente: "bg-red-500/20 text-red-400 border-red-500/50",
  cancelado: "bg-gray-500/20 text-gray-300 border-gray-500/50",
};

/** ¿La fecha del evento (YYYY-MM-DD) cae en el mes y año actuales? */
export const isEventThisMonth = (eventDate) => {
  if (!eventDate) return false;
  const d = new Date(eventDate + "T12:00:00");
  const now = new Date();
  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
};

export const filterByPaymentFilter = (contracts, filter) => {
  if (filter === "pendiente") return contracts.filter((c) => paymentStatus(c) === "pendiente");
  if (filter === "pagado") return contracts.filter((c) => paymentStatus(c) === "pagado");
  if (filter === "este_mes") return contracts.filter((c) => isEventThisMonth(c.event_date));
  return contracts;
};

/**
 * Ordena mostrando primero los eventos próximos (hoy o futuro, el más cercano primero);
 * los eventos ya pasados van después (el más reciente primero); sin fecha va al final.
 */
export const sortByUpcomingEventDate = (a, b) => {
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const rank = (c) => {
    if (!c.event_date) return 2;
    return c.event_date >= todayStr ? 0 : 1;
  };
  const rankA = rank(a), rankB = rank(b);
  if (rankA !== rankB) return rankA - rankB;
  if (rankA === 2) return 0;
  const diff = new Date(a.event_date) - new Date(b.event_date);
  return rankA === 0 ? diff : -diff;
};
